import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
    Box,
    Card,
    CardActionArea,
    Skeleton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

/**
 * One figure per card: a caption, the number in large type and, under it, the
 * figures that qualify it in one quiet line. The old summary cards stacked three
 * or four numbers of the same weight on a tinted fill, so nothing on the
 * dashboard read first.
 *
 * `to` makes the whole card a link to the list behind the figure; it is passed
 * only when the user can open that list.
 */
export default function DashboardKpiCard({
    title,
    value,
    tooltip,
    secondary,
    icon,
    color = 'primary',
    to,
    loading
}) {
    const body = (
        <Stack spacing={1.5} sx={{ p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <Box
                    aria-hidden
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: (theme) => theme.palette[color].soft,
                        color: (theme) => theme.palette[color].softText
                    }}
                >
                    <Iconify icon={icon} width={18} />
                </Box>
                {/* Wraps rather than ellipsising: "Balance — limits payins"
                    is the point of that card. */}
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ flex: 1, minWidth: 0 }}
                >
                    {title}
                </Typography>
                {tooltip && (
                    <Tooltip title={tooltip}>
                        <Box
                            component="span"
                            sx={{ display: 'flex', color: 'text.secondary' }}
                        >
                            <Iconify icon="eva:info-outline" width={16} />
                        </Box>
                    </Tooltip>
                )}
            </Stack>

            {loading ? (
                <Box>
                    <Skeleton variant="text" width="60%" height={40} />
                    <Skeleton variant="text" width="80%" height={20} />
                </Box>
            ) : (
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="h4"
                        component="p"
                        noWrap
                        title={String(value)}
                    >
                        {value}
                    </Typography>
                    {secondary && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            {secondary}
                        </Typography>
                    )}
                </Box>
            )}
        </Stack>
    );

    return (
        <Card sx={{ height: '100%' }}>
            {to ? (
                <CardActionArea
                    component={RouterLink}
                    to={to}
                    sx={{ height: '100%', alignItems: 'stretch' }}
                >
                    {body}
                </CardActionArea>
            ) : (
                body
            )}
        </Card>
    );
}

DashboardKpiCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.node,
    tooltip: PropTypes.string,
    /** Qualifying figures, already joined into one line. */
    secondary: PropTypes.node,
    /** An Iconify name. */
    icon: PropTypes.string.isRequired,
    /** A palette colour with a soft pair: primary, info, success, warning… */
    color: PropTypes.string,
    to: PropTypes.string,
    loading: PropTypes.bool
};

/** The KPI row: as many columns as fit at 220px, wrapping on narrow screens. */
export function DashboardKpiRow({ children }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
            }}
        >
            {children}
        </Box>
    );
}

DashboardKpiRow.propTypes = { children: PropTypes.node };
